import { t } from "@lingui/core/macro";
import { CellContext } from "@tanstack/react-table";

import { UserAvatar } from "_/components/avatar";
import { Button, ButtonGroup } from "_/components/button";
import { DateTime } from "_/components/date-time";
import { HandoffLink } from "_/components/handoff-link";
import { useModal, UseModalResult } from "_/components/modal";
import { ColumnDef, Table } from "_/components/table";
import { useAddToast } from "_/components/toasts";
import { testPermissions } from "_/components/permission-test";

import {
  Invitation,
  InvitationResponse,
  isInvitePending,
  useOrgInvites,
  useInviteMutations,
} from "_/data/invitations";
import { Org } from "_/data/orgs";
import {
  Role,
  translateRoleName,
  useCurrentUserPermissions,
} from "_/data/rbac";
import { useUsers } from "_/data/users";

import { Uuid } from "_/types";
import { indexById } from "_/utils";

import * as S from "./styled";

interface InviteLinkData {
  email: string;
  link: string;
}

// Shown instead of a toast when the server cannot email the invitation.
const InviteLinkModal = (props: UseModalResult<InviteLinkData>) => {
  const { Modal, modalData, closeModal } = props;

  return (
    <Modal title={t`components.invitations-list.invite-link.title`}>
      <HandoffLink
        description={t`components.invitations-list.invite-link.description ${modalData.email}`}
        link={modalData.link}
        onClose={closeModal}
      />
    </Modal>
  );
};

/**
 * Invitation list component for use in the admin panel for an organization.
 */
export const InvitationsList = ({ org }: { org: Org }) => {
  const { data: invitations = [], isLoading: invitationsLoading } =
    useOrgInvites(org.id);
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const isLoading = invitationsLoading || usersLoading;
  const { data: permissions } = useCurrentUserPermissions(org.domainId);
  const { resendInvite, revokeInvite, reviewInvite } = useInviteMutations();
  const addToast = useAddToast();
  const inviteLinkModal = useModal<InviteLinkData>();

  const usersById = indexById(users);

  // Helper to show a success toast.
  const toastSuccess = (title: string, content: string) => {
    addToast({ kind: "success", title, content });
  };

  // Helper for displaying error toast if an API request fails.
  const toastError = (err: Error) => {
    addToast({
      kind: "danger",
      title: "Operation failed",
      content: err.message,
    });

    // Rethrow the error so that the mutation can catch it (if needed) and the
    // success toast is not shown.
    throw err;
  };

  // Render function for displaying a user cell for the reviewed-by and
  // received-by columns.
  const userCell = ({ cell }: CellContext<Invitation, unknown>) => {
    const userId = cell.getValue<Uuid>();

    const user = usersById[userId];

    if (!user) {
      return "--";
    }

    return (
      <S.UserCell>
        <UserAvatar resource={user} size="small" />
        <span>{user.name}</span>
      </S.UserCell>
    );
  };

  const canReview = testPermissions("organizations/reviewMember", permissions);

  // After sending an invitation: show the link if the server returned one
  // (email disabled on this install), otherwise the usual success toast.
  const showSentInvite = (
    invitation: InvitationResponse,
    toastTitle: string,
    toastContent: string
  ) => {
    if (invitation.inviteLink) {
      inviteLinkModal.openModal({
        email: invitation.email,
        link: invitation.inviteLink,
      });
    } else {
      toastSuccess(toastTitle, toastContent);
    }
  };

  // Render function for actions cell of each row.
  const actionsCell = ({
    row: { original },
  }: CellContext<Invitation, unknown>) => {
    const params = { invitationId: original.id };
    const resend = async () => {
      const invitation = await resendInvite
        .mutateAsync(params)
        .catch(toastError);
      showSentInvite(
        invitation,
        t`components.invitations-list.resent-toast.title`,
        t`components.invitations-list.resent-toast.content ${original.email}`
      );
    };

    const revoke = async () => {
      await revokeInvite.mutateAsync(params).catch(toastError);
      toastSuccess(
        t`components.invitations-list.revoked-toast.title`,
        t`components.invitations-list.revoked-toast.content ${original.email}`
      );
    };

    const approve = async () => {
      const invitation = await reviewInvite
        .mutateAsync({ ...params, approval: true })
        .catch(toastError);
      showSentInvite(
        invitation,
        t`components.invitations-list.approved-toast.title`,
        t`components.invitations-list.approved-toast.content ${original.email}`
      );
    };

    const deny = async () => {
      await reviewInvite
        .mutateAsync({ ...params, approval: false })
        .catch(toastError);
      toastSuccess(
        t`components.invitations-list.denied-toast.title`,
        t`components.invitations-list.denied-toast.content ${original.email}`
      );
    };

    const buttons = original.approved ? (
      <>
        <Button
          size="small"
          kind="secondary"
          disabled={!canReview}
          onClick={() => void resend()}
        >
          {t`common.resend`}
        </Button>
        <Button
          size="small"
          kind="danger"
          disabled={!canReview}
          onClick={() => void revoke()}
        >
          {t`common.revoke`}
        </Button>
      </>
    ) : (
      <>
        <Button
          size="small"
          kind="secondary"
          disabled={!canReview}
          onClick={() => void approve()}
        >
          {t`common.approve`}
        </Button>
        <Button
          size="small"
          kind="secondary"
          disabled={!canReview}
          onClick={() => void deny()}
        >
          {t`common.deny`}
        </Button>
      </>
    );

    return <ButtonGroup>{buttons}</ButtonGroup>;
  };

  const columns: ColumnDef<Invitation>[] = [
    {
      id: "email",
      header: t`components.invitations-list.sent-to`,
      accessorKey: "email",
      cell: ({ cell }) => (
        <a
          target="_blank"
          href={`mailto:${cell.getValue<string>()}`}
          rel="noreferrer"
        >
          {cell.getValue<string>()}
        </a>
      ),
    },
    {
      id: "role",
      header: "Role",
      accessorKey: "role",
      cell: ({ cell }) => translateRoleName(cell.getValue<Role>()),
    },
    {
      id: "inviter",
      header: t`components.invitations-list.invited-by`,
      accessorKey: "invitedBy",
      cell: userCell,
    },
    {
      id: "reviewed",
      header: t`components.invitations-list.approved-by`,
      accessorKey: "reviewedBy",
      cell: userCell,
    },
    {
      id: "expiry",
      header: t`common.expiry`,
      accessorKey: "expiresAt",
      cell: ({ cell }) => (
        <DateTime value={cell.getValue<string>()} defaultValue="--" />
      ),
    },
    {
      id: "actions",
      header: t`common.actions`,
      cell: actionsCell,
    },
  ];

  const activeInvites = invitations.filter(isInvitePending);

  return (
    <>
      <Table columns={columns} data={activeInvites} loading={isLoading} />
      {inviteLinkModal.isModalOpen && <InviteLinkModal {...inviteLinkModal} />}
    </>
  );
};
